import type { FilterGroupMultiSelect, FilterOptionMultiSelect } from "~~/models/filters";
import type { PropertyFilterDto } from "~~/server/api/ecom/products/search.post";
import type {
  PlanktonProductSearchPropertyFilterResponse,
  PlanktonProductSearchPropertyFilterValueResponse,
  PlanktonProductSearchResponse,
  PlanktonProperty,
} from "~~/server/plankton";
import { NatchOSPlanktonImplementationModelsPropertyValueType } from "~~/server/plankton/schema";

type PropertyFilterGroupType = FilterGroupMultiSelect;

export const ThirteenMonthsInDays = 13 * 30;

export async function mapToPropertyFilters(
  response: PlanktonProductSearchResponse,
  activeFilters: Array<PropertyFilterDto> | undefined,
  locale: string,
): Promise<Array<PropertyFilterGroupType>> {
  const filterGroups = [] as Array<PropertyFilterGroupType>;

  if (!response.propertyFilters) {
    return filterGroups;
  }

  // console.log("[MapToPropertyFilters] Property filters response", response.propertyFilters);

  const propertyData = await $fetch("/api/ecom/properties/list");

  for (const propertyFilter of response.propertyFilters) {
    const filterOptions = propertyFilter.values
      ?.filter((v: PlanktonProductSearchPropertyFilterValueResponse) => (v.count ?? 0) > 0 || (v.countActive ?? 0) > 0);

    if (!filterOptions?.length) {
      continue;
    }

    const property = propertyData.properties.find((p: PlanktonProperty) => p.id === propertyFilter.propertyId);

    if (!property) {
      continue;
    }

    // selected list may only contain selected property filters which are valid for this filter group
    const selectedList = activeFilters
      ?.filter((pf: PropertyFilterDto) => filterOptions.map(o => o.propertyValueId).includes(pf.propertyValueId))
      .map((pf: PropertyFilterDto) => pf.propertyValueId);

    const filterGroup = {
      id: property.id,
      title: property.translations && property.translations[locale.substring(0, 2)]?.name || property.internalName,
      sortOrder: property.sortOrderValue,
      isHiddenInUI: false, // hiddenPropertyIdList.includes(property.id),
      selectedList: selectedList ?? [],
      options: [],
    } as FilterGroupMultiSelect;

    for (let idx = 0; idx < filterOptions.length; idx++) {
      const filterOption = filterOptions[idx];

      filterGroup.options.push({
        label: determineFilterLabel(propertyFilter, filterOption!),
        value: filterOption!.propertyValueId,
        count: filterOption!.count,
        sortOrder: filterOption!.sortOrder,
      } as FilterOptionMultiSelect);
    }

    // sort by order, then by option label
    filterGroup.options.sort((a: FilterOptionMultiSelect, b: FilterOptionMultiSelect) =>
      a.label.localeCompare(b.label));

    filterGroups.push(filterGroup);
  }

  // sort by order, then by filter group title
  return filterGroups.sort((a: PropertyFilterGroupType, b: PropertyFilterGroupType) =>
    a.sortOrder - b.sortOrder || a.title.localeCompare(b.title));
}

export function parsePropertyFilters(propertyFilters: string): Array<PropertyFilterDto> | undefined {
  try {
    if (!propertyFilters?.length) {
      return undefined;
    }

    return JSON.parse(propertyFilters) as Array<PropertyFilterDto>;
  }
  catch (e) {
    console.warn("Invalid property filters in query string", e);
    return undefined;
  }
}

function determineFilterLabel(
  propertyFilter: PlanktonProductSearchPropertyFilterResponse,
  filterOption: PlanktonProductSearchPropertyFilterValueResponse,
) {
  switch (propertyFilter!.valueType) {
    case NatchOSPlanktonImplementationModelsPropertyValueType.Boolean:
      return filterOption!.booleanValue;
    case NatchOSPlanktonImplementationModelsPropertyValueType.NumericValue:
      return filterOption!.numericValue;
    default:
      return filterOption!.textValue;
  }
}
